import { useState } from 'react';
import { CheckCircleIcon, ClockIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface ActivityStepProps {
  number: string;
  title: string;
  responsible: string;
  description: string;
  status: 'not-started' | 'in-progress' | 'completed';
  lastUpdate?: {
    role: string;
    timestamp: string;
  };
  isSubStep?: boolean;
}

export default function ActivityStep({
  number,
  title,
  responsible,
  description,
  status,
  lastUpdate,
  isSubStep = false,
}: ActivityStepProps) {
  const [currentStatus, setCurrentStatus] = useState(status);

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'in-progress':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <MinusCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return 'COMPLETED';
      case 'in-progress':
        return 'IN PROGRESS';
      default:
        return 'NOT STARTED';
    }
  };

  const handleStart = () => {
    setCurrentStatus('in-progress');
  };

  const handleComplete = () => {
    setCurrentStatus('completed');
  };

  const getStatusBackground = () => {
    switch (currentStatus) {
      case 'completed':
        return 'bg-[#E6F7FF]';
      case 'in-progress':
        return 'bg-[#FFFBE6]';
      default:
        return '';
    }
  };

  return (
    <div className={clsx(
      'border rounded-lg p-4 transition-colors duration-200',
      isSubStep ? 'ml-8' : '',
      getStatusBackground()
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-500">{number}</span>
            <h4 className="font-medium text-gray-900">{title}</h4>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium">Responsible:</span> {responsible}
          </p>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <div className="flex flex-col items-end space-y-2">
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className="text-sm font-medium">{getStatusText()}</span>
          </div>
          {lastUpdate && (<>
            <div className="mt-2 flex space-x-2">
              <button
                onClick={handleStart}
                disabled={currentStatus === 'in-progress' || currentStatus === 'completed'}
                className={clsx(
                  'px-3 py-1 text-sm rounded-md transition-colors duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'bg-indigo-600 text-white hover:bg-indigo-700'
                )}
              >Start</button>
              <button
                onClick={handleComplete}
                disabled={currentStatus !== 'in-progress'}
                className={clsx(
                  'px-3 py-1 text-sm rounded-md transition-colors duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'bg-green-600 text-white hover:bg-green-700'
                )}
              >Complete</button>
            </div>
            <p className="text-xs text-gray-500">
              Last update: {lastUpdate.role} - {lastUpdate.timestamp}
            </p>
          </>)}
        </div>
      </div>
    </div>
  );
}